import React, { useState, useMemo, useEffect } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import apiService from "../services/api";
import { MdCompare } from "react-icons/md";

const ProductsPageWrapper = styled.div`
  min-height: 60vh;

  .container {
    max-width: 1300px;
    margin: 0 auto;
    padding: 0 20px;
  }

  @media (max-width: 480px) {
    .container {
      padding: 0 10px;
    }
`;

const HeroBanner = styled.div`
  background: #ff6721;
  position: relative;
  overflow: hidden;
  height: 400px;
  display: flex;
  align-items: center;
  width: 100%;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0px;
    bottom: 60px;
    width: 50%;
    background-image: url(${(props) => props.bgImage});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.9;
    z-index: 1;
    border-radius: 0 0 0 50px;
  }

  @media (max-width: 768px) {
    height: 200px;

    &::before {
      width: 100%;
      opacity: 0.3;
    }
  }
`;

const HeroContent = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 40px 80px;
  position: relative;
  z-index: 2;
  width: 100%;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const Breadcrumb = styled.div`
  color: white;
  font-size: 14px;
  margin-bottom: 20px;
  opacity: 0.9;

  a {
    color: white;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  span {
    margin: 0 8px;
  }
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: 42px;
  font-weight: bold;
  margin-bottom: 15px;
  width: 100%;

  @media (max-width: 768px) {
    font-size: 28px;
    max-width: 100%;
  }
`;

const HeroDescription = styled.p`
  color: white;
  font-size: 16px;
  margin-bottom: 25px;
  max-width: 450px;
  line-height: 1.5;
  opacity: 0.95;

  @media (max-width: 768px) {
    max-width: 100%;
    font-size: 14px;
  }
`;

const HeroLink = styled(Link)`
  color: white;
  text-decoration: underline;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    opacity: 0.8;
  }
`;

const ProductsContainer = styled.div`
  display: flex;
  gap: 30px;
  width: 100%;
  padding: 40px 0;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const Sidebar = styled.div`
  width: 200px;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    width: 100%;
    display: ${(props) => (props.showFilters ? "block" : "none")};
  }
`;

const FilterSection = styled.div`
  background: #ffffffff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const FilterTitle = styled.h3`
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;
`;

const FilterOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
  cursor: pointer;

  input {
    margin: 0;
  }

  &:hover {
    color: #333;
  }
`;

const MainContent = styled.div`
  flex: 1;
`;

const ProductsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }
`;

const ResultsCount = styled.p`
  font-size: 14px;
  color: #666;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e5e5;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    // border-color: #ff6720;
  }
`;

const ProductImage = styled.div`
  height: 200px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding-left: 20px;
  padding-right: 20px;

  img {
    background: white;
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }

  @media (max-width: 480px) {
    height: 100px;
  }
`;

const ProductInfo = styled.div`
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 480px) {
    padding: 15px;
  }
`;

const ProductName = styled.h3`
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
  line-height: 1.3;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const ProductBadge = styled.div`
  background: #ff6720;
  color: white;
  width: fit-content;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 12px;
  display: inline-block;
`;

const ProductDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.4;
  margin-bottom: 15px;
  flex: 1;

  @media (max-width: 480px) {
    font-size: 13px;
    margingn-bottom: 0px;
  }
`;

const ProductActions = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
  justify-items: center;
  opacity: 1;
  transition: opacity 0.3s ease;

  @media (max-width: 480px) {
    margin-top: -10px;
    margin-bottom: 0px;
}
`;

const CompareSection = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: white;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background: #ff6720;
    transform: scale(1.1);

    svg {
      color: white;
    }
  }

  &.active {
    background: #ff6720;

    svg {
      color: white;
    }
  }
`;

const ComparisonBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-width: 1280px;
  margin: 0 auto;
  background: white;
  border-top: 1px solid #e0e0e0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  padding: 20px 20px;
  z-index: 1000;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  transition: transform 0.3s ease;
`;

const ComparisonContent = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 480px) {
    width: 100%;
    padding: 0px;
  }
`;

const ComparisonProductList = styled.div`
  display: flex;
  gap: 15px;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const ComparisonProductCard = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  // padding: 8px 12px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 700;
  color: #333333;
`;

const ProductThumbnail = styled.img`
  width: 140px;
  height: 110px;
  border-radius: 2px;
  object-fit: contains;

  @media (max-width: 480px) {
    width: 80px;
    height: 60px;
  }
`;

const RemoveButton = styled.button`
  background: #999;
  border: none;
  border-radius: 50px;
  texst-align: center;
  border-radius: 50px;
  color: #ffffffff;
  cursor: pointer;
  width: 20px;
  height: 20px;
  padding: 0;
  line-height: 16px;
  margin-left: 4px;
  font-size: 18px;
  font-weight: bold;

  &:hover {
    color: #000000ff;
  }
`;

const ComparisonBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 480px) {
    flex-direction: column;}
`;

const ComparisonTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;

  @media (max-width: 480px) {
    margin-bottom: 10px;
    display: none;
  }
`;

const ComparisonTitle1 = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
  display: none;

  @media (max-width: 480px) {
    margin-bottom: 10px;
    display: block;
  }
`;

const ComparisonActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CompareButton = styled.button`
  background: #ff6720;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #e55a1c;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ClearAllButton = styled.button`
  background: transparent;
  color: black;
  border: none;
  padding: 10px 0;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  transition: all 0.3s ease;

  &:hover {
    color: #e55a1c;
  }
`;

const ViewButton = styled(Link)`
  display: block;
  width: 100% !important;
  justify-content: center;
  padding: 12px 16px;
  background: #ff6720;
  color: white;
  border: none;
  border-radius: 50px;
  text-decoration: none;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: #e55a1c;
  }

  @media (max-width: 480px) {
    padding: 10px 12px;
    font-size: 13px;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;

  h3 {
    font-size: 24px;
    margin-bottom: 15px;
    color: #333;
  }

  p {
    font-size: 16px;
    margin-bottom: 30px;
  }
`;

const BnIcon = styled.div`
  color: #1b1b1bff;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
`;

// FAQ Component Styles

const FAQSection = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  width: 400px;
  max-height: calc(100vh - 40px);
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  z-index: 999;
  overflow: hidden;
  animation: slideInRight 0.3s ease-out;

  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    top: 20px;
    right: 20px;
    left: 20px;
    width: auto;
    max-height: calc(100vh - 40px);
  }
`;

const FAQHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;

  h3 {
    margin: 0;
    color: #333;
    font-size: 1.3rem;
    font-weight: 600;
  }
`;

const FAQCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: #e0e0e0;
    color: #333;
  }
`;

const FAQContent = styled.div`
  overflow-y: auto;
  max-height: calc(100vh - 120px);
  padding: 0;

  @media (max-width: 768px) {
    max-height: calc(100vh - 120px);
  }
`;

const FAQItem = styled.div`
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const FAQQuestion = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  cursor: pointer;
  font-weight: 600;
  color: #333;
  background: white;
  transition: all 0.3s ease;

  &:hover {
    background: #f8f9fa;
  }

  .icon {
    font-size: 20px;
    color: #ff6720;
    transition: transform 0.3s ease;
    font-weight: normal;
  }

  &.open .icon {
    transform: rotate(45deg);
  }
`;

const FAQAnswer = styled.div`
  padding: 0 20px;
  color: #555;
  line-height: 1.6;
  background: #fafafa;
  max-height: 0;
  overflow: hidden;
  transition: all 0.3s ease;

  &.open {
    max-height: 200px;
    padding: 20px;
  }
`;

const FilterControlsWrapper = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: row;
    gap: 10px;
    align-items: stretch;
    justify-content: space-between;
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: 1px solid #ddd;
  border-radius: 25px;
  background: white;
  color: #333;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: none;

  &:hover {
    border-color: #ff6720;
    color: #ff6720;
  }

  &:focus {
    outline: none;
    border-color: #ff6720;
  }

  @media (max-width: 768px) {
    display: flex;}
`;

const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: 1px solid #ddd;
  border-radius: 25px;
  background: white;
  color: #333;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    border-color: #ff6720;
    color: #ff6720;
  }

  &:focus {
    outline: none;
    border-color: #ff6720;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SortDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 150px;
  z-index: 100;
  margin-top: 4px;
  display: ${(props) => (props.isOpen ? "block" : "none")};
`;

const SortOption = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: white;
  color: #333;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  &.active {
    background-color: #ff6720;
    color: white;

    &:hover {
      background-color: #e55a1c;
    }
  }
`;

// Mock product data

const FAQComponent = ({ category, isVisible, onClose }) => {
  const [openItem, setOpenItem] = React.useState(null);

  const toggleItem = (index) => {
    setOpenItem(openItem === index ? null : index);
  };

  const categoryFAQs = {
    chainsaws: [
      {
        question: "What are chainsaws used for?",
        answer:
          "Chainsaws are versatile tools used for tree felling, limbing, pruning, cutting logs, and various landscaping tasks. They're essential for both professional and home use.",
      },
      {
        question:
          "What are the differences between electric and battery-operated chainsaws?",
        answer:
          "Electric chainsaws provide consistent power and are lighter, while battery-operated chainsaws offer portability without cords but have limited runtime depending on battery capacity.",
      },
      {
        question: "What are some safety features on chainsaws?",
        answer:
          "Modern chainsaws include chain brakes, throttle lockouts, anti-vibration systems, protective guards, and automatic oiling systems for safe operation.",
      },
      {
        question:
          "How do I choose the right chainsaw for my home improvement project?",
        answer:
          "Consider the size of trees you'll cut, frequency of use, your experience level, and whether you prefer corded, battery, or gas-powered models.",
      },
      {
        question: "Are STIHL chainsaws suitable for cutting large trees?",
        answer:
          "Yes, STIHL offers professional-grade chainsaws designed for heavy-duty work, including models specifically engineered for cutting large trees safely and efficiently.",
      },
    ],
    blowers: [
      {
        question: "What are leaf blowers used for?",
        answer:
          "Leaf blowers are used for clearing leaves, debris, and grass clippings from driveways, sidewalks, gardens, and hard-to-reach areas.",
      },
      {
        question: "What's the difference between corded and cordless blowers?",
        answer:
          "Corded blowers provide unlimited runtime with consistent power, while cordless models offer portability and freedom of movement.",
      },
      {
        question: "How do I maintain my leaf blower?",
        answer:
          "Regular maintenance includes cleaning air filters, checking spark plugs, inspecting fuel lines, and storing properly during off-season.",
      },
    ],
    brushcutters: [
      {
        question: "What are brushcutters used for?",
        answer:
          "Brushcutters are used for cutting thick grass, weeds, small bushes, and vegetation in areas where regular lawn mowers can't reach.",
      },
      {
        question:
          "What's the difference between string trimmers and brushcutters?",
        answer:
          "Brushcutters are more powerful and can handle thicker vegetation, while string trimmers are lighter and better for detailed trimming work.",
      },
      {
        question: "How do I choose the right cutting head?",
        answer:
          "Choose string heads for grass and light weeds, metal blades for thick brush and small trees, and plastic blades for general cutting tasks.",
      },
    ],
  };

  const currentFAQs = categoryFAQs[category] || categoryFAQs.chainsaws;

  if (!isVisible) return null;

  return (
    <FAQSection>
      <FAQHeader>
        <h3>FAQS:</h3>
        <FAQCloseButton onClick={onClose}>×</FAQCloseButton>
      </FAQHeader>
      <FAQContent>
        {currentFAQs.map((faq, index) => (
          <FAQItem key={index}>
            <FAQQuestion
              className={openItem === index ? "open" : ""}
              onClick={() => toggleItem(index)}
            >
              <span>{faq.question}</span>
              <span className="icon">+</span>
            </FAQQuestion>
            <FAQAnswer className={openItem === index ? "open" : ""}>
              {faq.answer}
            </FAQAnswer>
          </FAQItem>
        ))}
      </FAQContent>
    </FAQSection>
  );
};

const ProductsPage = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const typeFromUrl = searchParams.get("type");
  const [filters, setFilters] = useState({
    productType: [],
    cordedCordless: "",
    batteryAmpHours: "",
    brand: "",
    category: "",
  });
  const [productTypeSearch, setProductTypeSearch] = useState("");
  const [sortBy, setSortBy] = useState("relevance");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [compareList, setCompareList] = useState([]);
  const [showFAQ, setShowFAQ] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const maxCompare = isMobile ? 2 : 4;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initial call to set correct state
    return () => window.removeEventListener("resize", handleResize);
  }, [compareList]); // Add compareList as dependency

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showSortDropdown &&
        !event.target.closest(".sort-dropdown-container")
      ) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSortDropdown]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiService.getProductsByCategory(category);
        console.log("API Response:", response);

        if (response.success) {
          setProducts(response.data || []);
        } else {
          setError("Failed to load products");
          // Fallback to mock data for the category
        }
      } catch (err) {
        console.error("Failed to load products:", err);
        setError(err.message);
        // Fallback to mock data for the category
      } finally {
        setLoading(false);
      }
    };

    if (category) {
      loadProducts();
    }
    if (typeFromUrl) {
      setFilters((prev) => ({
        ...prev,
        cordedCordless: typeFromUrl,
      }));
    }
  }, [category, typeFromUrl]);

  // Update page title based on category
  useEffect(() => {
    if (category) {
      const categoryTitle =
        category.charAt(0).toUpperCase() + category.slice(1).replace(/-/g, " ");
      document.title = `${categoryTitle} | XPLOD-GROUPS`;
    } else {
      document.title = "Products | XPLOD-GROUPS";
    }

    return () => {
      document.title = "Home | XPLOD-GROUPS";
    };
  }, [category]);

  const categoryTitles = {
    chainsaws: "Chainsaws",
    brushcutters: "Brushcutters",
    blowers: "Blowers",
    "pressure-washers": "Pressure Washers",
    "lawn-mowers": "Lawn Mowers",
  };

  const categoryDescriptions = {
    chainsaws: "Browse our chainsaws collection.",
    brushcutters: "Professional brushcutters for tough cutting jobs.",
    blowers: "Powerful blowers for yard cleanup and maintenance.",
    "pressure-washers": "High-performance pressure washers for cleaning tasks.",
    "lawn-mowers": "Quality lawn mowers for lawn care and maintenance.",
  };

  const categoryHeroData = {
    chainsaws: {
      title: "Chainsaws",
      description:
        "Tackle tough cutting jobs with BLACK+DECKER's electric and cordless chainsaws. Find durable, high-performance models ideal for home improvement projects.",
      image:
        "https://images.unsplash.com/photo-1504148455328-c376907d081c?w=800&h=400&fit=crop&q=80",
      faqLink: "Electric Chainsaws | Reciprocating Saws FAQs",
    },
    brushcutters: {
      title: "Brushcutters | String Trimmers",
      description:
        "Tackle tough cutting jobs with BLACK+DECKER's professional brushcutters. Find durable, high-performance models ideal for landscaping and yard maintenance.",
      image:
        "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&h=400&fit=crop&q=80",
      faqLink: "Brushcutters | String Trimmers FAQs",
    },
    blowers: {
      title: "Leaf Blowers | Yard Cleanup Tools",
      description:
        "Tackle tough cleanup jobs with BLACK+DECKER's powerful leaf blowers. Find durable, high-performance models ideal for yard maintenance and debris removal.",
      image:
        "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=400&fit=crop&q=80",
      faqLink: "Leaf Blowers | Yard Cleanup Tools FAQs",
    },
    "pressure-washers": {
      title: "Pressure Washers | Cleaning Equipment",
      description:
        "Tackle tough cleaning jobs with BLACK+DECKER's powerful pressure washers. Find durable, high-performance models ideal for home and outdoor cleaning projects.",
      image:
        "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=800&h=400&fit=crop&q=80",
      faqLink: "Pressure Washers | Cleaning Equipment FAQs",
    },
    "lawn-mowers": {
      title: "Lawn Mowers | Grass Cutting Equipment",
      description:
        "Tackle tough lawn care jobs with BLACK+DECKER's reliable lawn mowers. Find durable, high-performance models ideal for residential and commercial use.",
      image:
        "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&h=400&fit=crop&q=80",
      faqLink: "Lawn Mowers | Grass Cutting Equipment FAQs",
    },
  };

  const currentHeroData = categoryHeroData[category] || {
    title: categoryTitles[category] || category.replace("-", " "),
    description:
      categoryDescriptions[category] ||
      `Browse our ${category.replace("-", " ")} collection.`,
    image:
      "https://images.unsplash.com/photo-1504148455328-c376907d081c?w=800&h=400&fit=crop&q=80",
    faqLink: `${categoryTitles[category] || category} FAQs`,
  };

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...products];

    // Apply product type filter
    if (filters.productType.length > 0) {
      filtered = filtered.filter((product) =>
        filters.productType.some((type) => {
          // Check product_type field
          if (
            product.product_type &&
            product.product_type.toLowerCase() === type.toLowerCase()
          ) {
            return true;
          }

          // Check category field
          if (
            product.category &&
            product.category.toLowerCase() === type.toLowerCase()
          ) {
            return true;
          }

          // Check subcategory field
          if (
            product.subcategory &&
            product.subcategory.toLowerCase() === type.toLowerCase()
          ) {
            return true;
          }

          // Check product name with precise word matching
          const productName = product.name.toLowerCase();
          const typeToCheck = type.toLowerCase();

          const nameWords = productName.split(/[\s\-_,|]+/);
          const typeWords = typeToCheck.split(/[\s\-_,|]+/);

          return typeWords.every((typeWord) =>
            nameWords.some(
              (nameWord) =>
                nameWord.includes(typeWord) || typeWord.includes(nameWord),
            ),
          );
        }),
      );
    }

    // Apply power type filter
    if (filters.cordedCordless) {
      filtered = filtered.filter((product) => {
        // First check the 'type' field directly (most reliable)
        if (product.type) {
          const typeValue = product.type.toLowerCase();
          const filterValue = filters.cordedCordless.toLowerCase();

          // Direct match on type field
          if (typeValue.includes(filterValue)) {
            return true;
          }
        }

        // Then check in text content
        const searchText =
          `${product.name || ""} ${product.description || ""} ${product.brief_description || ""} ${product.type || ""} ${product.brand || ""}`.toLowerCase();

        const searchTerms = {
          cordless: ["cordless", "battery", "20v", "18v", "12v", "lithium"],
          corded: ["corded", "electric", "plug-in"],
          petrol: ["petrol", "gas", "gasoline", "2-stroke", "4-stroke", "cc"],
        };

        return (
          searchTerms[filters.cordedCordless]?.some((term) =>
            searchText.includes(term),
          ) || false
        );
      });
    }

    // Apply battery amp hours filter
    if (filters.batteryAmpHours) {
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(filters.batteryAmpHours) ||
          (product.description &&
            typeof product.description === "string" &&
            product.description
              .toLowerCase()
              .includes(filters.batteryAmpHours)),
      );
    }

    // Apply category filter
    if (filters.category) {
      filtered = filtered.filter(
        (product) => product.category === filters.category,
      );
    }

    // Sort products
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "relevance":
          return a.name.localeCompare(b.name);
        case "a-to-z":
          return a.name.localeCompare(b.name);
        case "z-to-a":
          return b.name.localeCompare(a.name);
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return filtered;
  }, [products, filters, sortBy]);

  const handleFilterChange = (filterType, value) => {
    if (filterType === "productType") {
      setFilters((prev) => ({
        ...prev,
        [filterType]: prev[filterType].includes(value)
          ? prev[filterType].filter((item) => item !== value)
          : [...prev[filterType], value],
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        [filterType]: prev[filterType] === value ? "" : value,
      }));
    }
  };

  // Get dynamic product types from actual products - extract from database
  const getProductTypesFromProducts = () => {
    const types = new Set();

    products.forEach((product) => {
      // Extract from product.product_type if it exists
      if (product.product_type) {
        types.add(product.product_type);
      }

      // Extract from product.category if it exists
      if (product.category && product.category !== category) {
        types.add(product.category);
      }

      // Extract from product.subcategory if it exists
      if (product.subcategory) {
        types.add(product.subcategory);
      }

      // Smart extraction from product names - extract the main product type
      const productName = product.name.toLowerCase().trim();

      // Split by common delimiters and take meaningful parts
      const nameParts = productName
        .split(/[\s\-_,|]+/)
        .filter((part) => part.length > 2);

      // Common tool type keywords to extract
      const toolKeywords = [
        "chainsaw",
        "saw",
        "cutter",
        "trimmer",
        "blower",
        "mower",
        "washer",
        "drill",
        "grinder",
        "sander",
        "router",
        "planer",
        "nailer",
        "stapler",
        "vacuum",
        "cleaner",
        "polisher",
        "buffer",
        "edger",
        "hedge",
        "leaf",
        "pressure",
        "brush",
        "pole",
        "lopper",
        "shears",
        "pruner",
      ];

      // Extract tool types from product name
      nameParts.forEach((part) => {
        if (toolKeywords.some((keyword) => part.includes(keyword))) {
          // Capitalize first letter
          const cleanType = part.charAt(0).toUpperCase() + part.slice(1);
          types.add(cleanType);
        }
      });

      // Extract compound tool types
      if (
        productName.includes("pole saw") ||
        productName.includes("pole-saw")
      ) {
        types.add("Pole Saw");
      }
      if (
        productName.includes("hedge trimmer") ||
        productName.includes("hedge-trimmer")
      ) {
        types.add("Hedge Trimmer");
      }
      if (
        productName.includes("leaf blower") ||
        productName.includes("leaf-blower")
      ) {
        types.add("Leaf Blower");
      }
      if (
        productName.includes("pressure washer") ||
        productName.includes("pressure-washer")
      ) {
        types.add("Pressure Washer");
      }
      if (
        productName.includes("lawn mower") ||
        productName.includes("lawn-mower")
      ) {
        types.add("Lawn Mower");
      }
      if (
        productName.includes("brush cutter") ||
        productName.includes("brush-cutter")
      ) {
        types.add("Brush Cutter");
      }
    });

    return Array.from(types).sort();
  };

  const productTypes = getProductTypesFromProducts();
  const filteredProductTypes = productTypes.filter((type) =>
    type.toLowerCase().includes(productTypeSearch.toLowerCase()),
  );

  const getProductTypeCount = (type) => {
    return products.filter((product) => {
      // Check product_type field
      if (
        product.product_type &&
        product.product_type.toLowerCase() === type.toLowerCase()
      ) {
        return true;
      }

      // Check category field
      if (
        product.category &&
        product.category.toLowerCase() === type.toLowerCase()
      ) {
        return true;
      }

      // Check subcategory field
      if (
        product.subcategory &&
        product.subcategory.toLowerCase() === type.toLowerCase()
      ) {
        return true;
      }

      // Check product name (more precise matching)
      const productName = product.name.toLowerCase();
      const typeToCheck = type.toLowerCase();

      // Exact word matching to avoid false positives
      const nameWords = productName.split(/[\s\-_,|]+/);
      const typeWords = typeToCheck.split(/[\s\-_,|]+/);

      // Check if all type words are found in name words
      const hasAllTypeWords = typeWords.every((typeWord) =>
        nameWords.some(
          (nameWord) =>
            nameWord.includes(typeWord) || typeWord.includes(nameWord),
        ),
      );

      return hasAllTypeWords;
    }).length;
  };

  const getPowerTypeCount = (type) => {
    return products.filter((product) => {
      // Check type field directly first (most reliable)
      if (product.type) {
        const typeValue = product.type.toLowerCase();
        if (
          type === "petrol" &&
          (typeValue.includes("petrol") || typeValue.includes("gas"))
        ) {
          return true;
        }
        if (
          type === "cordless" &&
          (typeValue.includes("cordless") || typeValue.includes("battery"))
        ) {
          return true;
        }
        if (
          type === "corded" &&
          (typeValue.includes("corded") || typeValue.includes("electric"))
        ) {
          return true;
        }
      }

      // Then check in text content
      const searchText =
        `${product.name || ""} ${product.description || ""} ${product.brief_description || ""} ${product.type || ""} ${product.brand || ""}`.toLowerCase();

      const searchTerms = {
        cordless: ["cordless", "battery", "20v", "18v", "12v", "lithium"],
        corded: ["corded", "electric", "plug-in", "cable"],
        petrol: ["petrol", "gas", "gasoline", "2-stroke", "4-stroke", "cc"],
      };

      return (
        searchTerms[type]?.some((term) => searchText.includes(term)) || false
      );
    }).length;
  };

  const getBatteryAmpHoursCount = (ampHours) => {
    return products.filter((product) => {
      const searchText =
        `${product.name} ${product.description || ""} ${product.brief_description || ""}`.toLowerCase();
      return searchText.includes(ampHours.toLowerCase());
    }).length;
  };

  // Dynamically extract power types from actual products
  const getAvailablePowerTypes = () => {
    const powerTypes = new Set();

    products.forEach((product) => {
      // First check the 'type' field directly (this is most reliable)
      if (product.type) {
        const typeValue = product.type.toLowerCase();
        if (typeValue.includes("petrol") || typeValue.includes("gas")) {
          powerTypes.add("petrol");
        }
        if (typeValue.includes("cordless") || typeValue.includes("battery")) {
          powerTypes.add("cordless");
        }
        if (typeValue.includes("corded") || typeValue.includes("electric")) {
          powerTypes.add("corded");
        }
      }

      // Then check text content as fallback
      const searchText =
        `${product.name || ""} ${product.description || ""} ${product.brief_description || ""} ${product.type || ""} ${product.brand || ""}`.toLowerCase();

      const cordlessTerms = [
        "cordless",
        "battery",
        "20v",
        "18v",
        "12v",
        "lithium",
      ];
      if (cordlessTerms.some((term) => searchText.includes(term))) {
        powerTypes.add("cordless");
      }

      const cordedTerms = ["corded", "electric", "plug-in", "cable"];
      if (cordedTerms.some((term) => searchText.includes(term))) {
        powerTypes.add("corded");
      }

      const petrolTerms = [
        "petrol",
        "gas",
        "gasoline",
        "2-stroke",
        "4-stroke",
        "cc",
      ];
      if (petrolTerms.some((term) => searchText.includes(term))) {
        powerTypes.add("petrol");
      }
    });

    return Array.from(powerTypes).sort();
  };

  const availablePowerTypes = getAvailablePowerTypes();
  const availableBatteryOptions = ["1.5 ah", "2 ah"].filter(
    (option) => getBatteryAmpHoursCount(option) > 0,
  );
  const hasCordlessProducts = getPowerTypeCount("cordless") > 0;

  const handleCompareToggle = (id) => {
    if (compareList.includes(id)) {
      setCompareList(compareList.filter((pid) => pid !== id));
    } else if (compareList.length < maxCompare) {
      setCompareList([...compareList, id]);
    }
    // Optional: Add feedback if max is reached
    // else alert("Maximum products for comparison reached.");
  };

  const handleRemoveFromCompare = (productId) => {
    setCompareList((prev) => prev.filter((id) => id !== productId));
  };

  const handleClearAll = () => {
    setCompareList([]);
  };

  const handleCompareProducts = () => {
    if (compareList.length >= 2) {
      navigate(`/compare?products=${compareList.join(",")}`);
    } else {
      alert("Please select at least 2 products to compare");
    }
  };

  const getSelectedProducts = () => {
    return filteredAndSortedProducts.filter((product) =>
      compareList.includes(product.id),
    );
  };

  if (loading) {
    return (
      <ProductsPageWrapper>
        <div className="container">
          <div style={{ textAlign: "center", padding: "100px 20px" }}>
            <h2>Loading Products...</h2>
            <p>Fetching {categoryTitles[category] || category} products...</p>
          </div>
        </div>
      </ProductsPageWrapper>
    );
  }

  if (!category) {
    return (
      <ProductsPageWrapper>
        <div className="container">
          <NoResults>
            <h3>Category Not Found</h3>
            <p>Please select a valid category.</p>
            <Link to="/">Return to Home</Link>
          </NoResults>
        </div>
      </ProductsPageWrapper>
    );
  }

  return (
    <>
      <HeroBanner bgImage={currentHeroData.image}>
        <HeroContent>
          <Breadcrumb>
            <Link to="/">Home</Link>
            <span>&gt;</span>
            <span>{currentHeroData.title}</span>
          </Breadcrumb>
          <HeroTitle>{currentHeroData.title}</HeroTitle>
          <HeroDescription>{currentHeroData.brief_description}</HeroDescription>
          <HeroLink onClick={() => setShowFAQ(!showFAQ)}>
            {currentHeroData.faqLink}
          </HeroLink>
        </HeroContent>
      </HeroBanner>

      <FAQComponent
        category={category}
        isVisible={showFAQ}
        onClose={() => setShowFAQ(false)}
      />

      <ProductsPageWrapper>
        <div className="container">
          {error && (
            <div
              style={{
                color: "#dc2626",
                marginTop: "20px",
                textAlign: "center",
              }}
            >
              ⚠️ API Issue: {error} (Showing cached data)
            </div>
          )}

          <ProductsContainer>
            <Sidebar showFilters={showFilters}>
              <FilterSection>
                <FilterTitle>Product Type</FilterTitle>
                <div style={{ marginBottom: "15px" }}>
                  <input
                    type="text"
                    placeholder="Search Product Type"
                    value={productTypeSearch}
                    onChange={(e) => setProductTypeSearch(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #b3b3b3ff",
                      borderRadius: "50px",
                      fontSize: "14px",
                    }}
                  />
                </div>
                <FilterOptions>
                  {filteredProductTypes.map((type) => (
                    <FilterOption key={type}>
                      <input
                        type="checkbox"
                        checked={filters.productType.includes(type)}
                        onChange={() => handleFilterChange("productType", type)}
                      />
                      {type} ({getProductTypeCount(type)})
                    </FilterOption>
                  ))}
                </FilterOptions>
              </FilterSection>

              {availablePowerTypes.length > 0 && (
                <FilterSection>
                  <FilterTitle>Power Type</FilterTitle>
                  <FilterOptions>
                    {availablePowerTypes.map((type) => {
                      // Map internal type names to display names
                      const displayNames = {
                        corded: "Electric",
                        cordless: "Battery",
                        petrol: "Petrol",
                      };

                      const displayName =
                        displayNames[type] ||
                        type.charAt(0).toUpperCase() + type.slice(1);

                      return (
                        <FilterOption key={type}>
                          <input
                            type="checkbox"
                            checked={filters.cordedCordless === type}
                            onChange={() =>
                              handleFilterChange("cordedCordless", type)
                            }
                          />
                          {displayName} ({getPowerTypeCount(type)})
                        </FilterOption>
                      );
                    })}
                  </FilterOptions>
                </FilterSection>
              )}

              {hasCordlessProducts && availableBatteryOptions.length > 0 && (
                <FilterSection>
                  <FilterTitle>Battery Amp Hours (Ah)</FilterTitle>
                  <FilterOptions>
                    {availableBatteryOptions.map((option) => (
                      <FilterOption key={option}>
                        <input
                          type="checkbox"
                          checked={filters.batteryAmpHours === option}
                          onChange={() =>
                            handleFilterChange("batteryAmpHours", option)
                          }
                        />
                        {option.toUpperCase()} (
                        {getBatteryAmpHoursCount(option)})
                      </FilterOption>
                    ))}
                  </FilterOptions>
                </FilterSection>
              )}
            </Sidebar>

            <MainContent>
              <ProductsHeader>
                <ResultsCount>
                  Showing {filteredAndSortedProducts.length} of{" "}
                  {products.length} products
                </ResultsCount>
                <FilterControlsWrapper>
                  <FilterButton onClick={() => setShowFilters(!showFilters)}>
                    <span>Filters</span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M12 6V4m0 2v2m0-2h4.5M7.5 6H3m9 6v-2m0 2v2m0-2h4.5M7.5 12H3m9 6v-2m0 2v2m0-2h4.5M7.5 18H3" />
                    </svg>
                  </FilterButton>

                  <div
                    style={{ position: "relative" }}
                    className="sort-dropdown-container"
                  >
                    <SortButton
                      onClick={() => setShowSortDropdown(!showSortDropdown)}
                    >
                      <span>
                        {sortBy === "relevance"
                          ? "Relevance"
                          : sortBy === "a-to-z"
                            ? "A to Z"
                            : "Z to A"}
                      </span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </SortButton>
                    <SortDropdown isOpen={showSortDropdown}>
                      <SortOption
                        className={sortBy === "relevance" ? "active" : ""}
                        onClick={() => {
                          setSortBy("relevance");
                          setShowSortDropdown(false);
                        }}
                      >
                        Relevance
                      </SortOption>
                      <SortOption
                        className={sortBy === "a-to-z" ? "active" : ""}
                        onClick={() => {
                          setSortBy("a-to-z");
                          setShowSortDropdown(false);
                        }}
                      >
                        A to Z
                      </SortOption>
                      <SortOption
                        className={sortBy === "z-to-a" ? "active" : ""}
                        onClick={() => {
                          setSortBy("z-to-a");
                          setShowSortDropdown(false);
                        }}
                      >
                        Z to A
                      </SortOption>
                    </SortDropdown>
                  </div>
                </FilterControlsWrapper>
              </ProductsHeader>

              {filteredAndSortedProducts.length === 0 ? (
                <NoResults>
                  <h3>No Products Found</h3>
                  <p>
                    No products available in this category. Try browsing other
                    categories.
                  </p>
                </NoResults>
              ) : (
                <ProductGrid>
                  {filteredAndSortedProducts.map((product) => (
                    <ProductCard key={product.id}>
                      <ProductImage>
                        <CompareSection
                          className={
                            compareList.includes(product.id) ? "active" : ""
                          }
                          onClick={() => handleCompareToggle(product.id)}
                          title="Add to compare"
                        >
                          <BnIcon>
                            <MdCompare />
                          </BnIcon>
                        </CompareSection>
                        <img
                          src={
                            product.images?.[0]?.image_url ||
                            product.image ||
                            "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=280&h=200&fit=crop"
                          }
                          alt={product.images?.[0]?.alt_text || product.name}
                        />
                      </ProductImage>
                      <ProductInfo>
                        <ProductBadge>Best Seller</ProductBadge>
                        <ProductName>{product.name}</ProductName>
                        <ProductDescription>
                          {(
                            product.brief_description ||
                            product.description ||
                            `High-quality ${product.name.toLowerCase()} with professional features and reliable performance.`
                          )
                            .split(" ")
                            .slice(0, 8)
                            .join(" ") + "..."}
                        </ProductDescription>
                        <ProductActions className="product-actions">
                          <ViewButton to={`/products/${product.id}`}>
                            View Details
                          </ViewButton>
                        </ProductActions>
                      </ProductInfo>
                    </ProductCard>
                  ))}
                </ProductGrid>
              )}
            </MainContent>
          </ProductsContainer>
        </div>
      </ProductsPageWrapper>
      {compareList.length > 0 && (
      <ComparisonBar>
        <ComparisonContent>
          {/* Products on top */}
          <ComparisonTitle1>Product Comparison ({compareList.length}/{maxCompare})</ComparisonTitle1>
          <ComparisonProductList>
            {getSelectedProducts().map((product) => (
              <ComparisonProductCard key={product.id}>
                <ProductThumbnail
                  src={
                    product.images?.[0]?.image_url ||
                    product.image ||
                    "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=40&h=40&fit=crop"
                  }
                  alt={product.name}
                />
                <span>
                  {product.name.length > 20
                    ? `${product.name.substring(0, 20)}...`
                    : product.name}
                </span>
                <RemoveButton
                  onClick={() => handleRemoveFromCompare(product.id)}
                  title="Remove from comparison"
                >
                  ×
                </RemoveButton>
              </ComparisonProductCard>
            ))}

            {/* Add empty slots for remaining products */}
            {Array.from({ length: maxCompare - compareList.length }).map(
              (_, index) => (
                <div
                  key={`empty-${index}`}
                  style={{
                    border: "2px dashed #ddd",
                    borderRadius: "4px",
                    padding: "8px 12px",
                    minWidth: "120px",
                    textAlign: "center",
                    color: "#999",
                    fontSize: "14px",
                  }}
                >
                  Add Product
                </div>
              ),
            )}
          </ComparisonProductList>

          {/* Comparison info and actions on bottom */}
          <ComparisonBottom>
            <ComparisonTitle>
              Product Comparison ({compareList.length}/{maxCompare})
            </ComparisonTitle>

            <ComparisonActions>
              <CompareButton
                onClick={handleCompareProducts}
                disabled={compareList.length < 2}
              >
                Compare Products
              </CompareButton>
              <ClearAllButton onClick={handleClearAll}>
                Clear All
              </ClearAllButton>
            </ComparisonActions>
          </ComparisonBottom>
        </ComparisonContent>
      </ComparisonBar>
    )}
    </>
  );
};

export default ProductsPage;
